import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { FlaskConical, Percent, Clock, Trophy, MoreVertical, Settings } from 'lucide-react';

/**
 * ABTestNode - Visual node for A/B test container in funnel builder
 */
const ABTestNode = memo(({ data, selected }) => {
    const {
        name = 'A/B Test',
        parentAdSetId,
        trafficSplit = [50, 50],
        winnerCriteria = 'CTR',
        duration = 14,
        autoPauseLoser = true,
        variantIds = [],
        status = 'draft',
        onEdit,
        onMenuClick
    } = data;

    const statusColors = {
        draft: 'var(--color-text-tertiary)',
        running: 'var(--color-warning)',
        completed: 'var(--color-success)',
        paused: 'var(--color-error)'
    };

    const criteriaLabels = {
        CTR: 'Click-Through Rate',
        CPA: 'Cost Per Acquisition',
        ROAS: 'Return on Ad Spend',
        conversions: 'Total Conversions',
        impressions: 'Impressions'
    };

    // Generate variant labels (A, B, C, etc.)
    const getVariantLabel = (index) => String.fromCharCode(65 + index);

    return (
        <div
            style={{
                background: 'var(--color-bg-primary)',
                border: selected
                    ? '2px solid var(--color-warning)'
                    : '2px dashed var(--color-warning)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)',
                minWidth: '300px',
                boxShadow: selected ? 'var(--shadow-lg)' : 'var(--shadow-md)',
                transition: 'all var(--transition-fast)'
            }}
        >
            {/* Target handle (top) - connects from Ad Set */}
            <Handle
                type="target"
                position={Position.Top}
                id="adset-in"
                style={{
                    background: 'var(--color-info)',
                    width: '10px',
                    height: '10px'
                }}
            />

            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-3)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--color-warning-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <FlaskConical size={18} style={{ color: 'var(--color-warning)' }} />
                    </div>
                    <div>
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-warning)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontWeight: 600
                        }}>
                            A/B Test
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {name}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    {/* Status indicator */}
                    <span style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-full)',
                        background: `${statusColors[status]}20`,
                        color: statusColors[status],
                        fontWeight: 500,
                        textTransform: 'capitalize'
                    }}>
                        {status}
                    </span>

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onMenuClick?.();
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                            borderRadius: 'var(--radius-sm)',
                            color: 'var(--color-text-tertiary)',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <MoreVertical size={14} />
                    </button>
                </div>
            </div>

            {/* Traffic split visualization */}
            <div style={{
                marginBottom: 'var(--space-3)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-2)'
                }}>
                    <Percent size={12} style={{ color: 'var(--color-text-tertiary)' }} />
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)'
                    }}>
                        Traffic Split
                    </span>
                </div>

                {/* Split bar */}
                <div style={{
                    display: 'flex',
                    borderRadius: 'var(--radius-full)',
                    overflow: 'hidden',
                    height: '24px',
                    background: 'var(--color-bg-tertiary)'
                }}>
                    {trafficSplit.map((percent, index) => {
                        const colors = [
                            'var(--color-primary)',
                            'var(--color-info)',
                            'var(--color-success)',
                            'var(--color-warning)'
                        ];
                        return (
                            <div
                                key={index}
                                style={{
                                    width: `${percent}%`,
                                    background: colors[index % colors.length],
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 600
                                }}
                            >
                                {getVariantLabel(index)} ({percent}%)
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Test settings */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-3)'
            }}>
                {/* Winner criteria */}
                <div style={{
                    padding: 'var(--space-2)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        marginBottom: '2px'
                    }}>
                        <Trophy size={10} style={{ color: 'var(--color-warning)' }} />
                        <span style={{
                            fontSize: '10px',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            Winner By
                        </span>
                    </div>
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)'
                    }}>
                        {criteriaLabels[winnerCriteria] || winnerCriteria}
                    </span>
                </div>

                {/* Duration */}
                <div style={{
                    padding: 'var(--space-2)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        marginBottom: '2px'
                    }}>
                        <Clock size={10} style={{ color: 'var(--color-text-tertiary)' }} />
                        <span style={{
                            fontSize: '10px',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            Duration
                        </span>
                    </div>
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)'
                    }}>
                        {duration} days
                    </span>
                </div>
            </div>

            {/* Variants count and auto-pause */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 'var(--font-size-xs)'
            }}>
                <div style={{ color: 'var(--color-text-secondary)' }}>
                    {variantIds.length} variant{variantIds.length !== 1 ? 's' : ''} configured
                </div>

                {autoPauseLoser && (
                    <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        color: 'var(--color-success)',
                        fontSize: '10px'
                    }}>
                        <Settings size={10} />
                        Auto-pause loser
                    </span>
                )}
            </div>

            {/* Main source handle (bottom center) - for connecting to ads */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="variant-out"
                style={{
                    background: 'var(--color-warning)',
                    width: '10px',
                    height: '10px'
                }}
            />
        </div>
    );
});

ABTestNode.displayName = 'ABTestNode';

export default ABTestNode;
